import { useRef, useEffect, useState } from "react";
import { Link } from "wouter";
import PageMeta from "../components/PageMeta";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
  useMotionValue,
  useInView,
} from "framer-motion";
import { ArrowRight } from "lucide-react";
import Lenis from "lenis";
import WaitlistModal from "@/components/WaitlistModal";

/* ─── CURSOR ────────────────────────────────────────────────────────────────
   Instant dot (no spring) + subtle trailing ring with gentle spring.        */
function Cursor() {
  const mx = useMotionValue(-200);
  const my = useMotionValue(-200);

  useEffect(() => {
    const move = (e: MouseEvent) => { mx.set(e.clientX); my.set(e.clientY); };
    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, []);

  return (
    <motion.div
      className="fixed pointer-events-none z-[9999]"
      style={{ x: mx, y: my, translateX: "-50%", translateY: "-50%" }}
    >
      <div className="w-2 h-2 rounded-full bg-primary" />
    </motion.div>
  );
}

/* ─── HERO IMAGE (desktop side panel) ────────────────────────────────────
   Pans via backgroundPositionX — image stays fixed, visible window slides. */
function HeroImage() {
  const bgUrl = `${import.meta.env.BASE_URL}wall-4.png`;
  return (
    <motion.div
      className="absolute inset-0"
      style={{
        backgroundImage: `url("${bgUrl}")`,
        backgroundSize: "220% auto",
        backgroundRepeat: "no-repeat",
        backgroundPositionY: "center",
      }}
      animate={{ backgroundPositionX: ["0%", "100%"] }}
      transition={{ duration: 52, ease: "linear", repeat: Infinity, repeatType: "loop" }}
    />
  );
}

/* ─── HERO IMAGE (mobile, portrait, background-position pan) ─────────────
   Image element stays fixed — only the visible region slides left → right.
   aspect-[3/4] gives a proper portrait crop on mobile.                    */
function HeroMobileImage() {
  const bgUrl = `${import.meta.env.BASE_URL}wall-4.png`;
  return (
    <div className="relative w-full overflow-hidden" style={{ aspectRatio: "4/3" }}>
      <motion.div
        className="absolute inset-0"
        style={{
          backgroundImage: `url("${bgUrl}")`,
          backgroundSize: "220% auto",
          backgroundRepeat: "no-repeat",
          backgroundPositionY: "30%",
        }}
        animate={{ backgroundPositionX: ["0%", "100%"] }}
        transition={{ duration: 50, ease: "linear", repeat: Infinity, repeatType: "loop" }}
      />
      <div className="absolute inset-0 bg-primary/10" />
    </div>
  );
}

/* ─── CLINIC CAROUSEL ────────────────────────────────────────────────────
   Direction-aware AnimatePresence + Ken Burns + rAF progress bars.       */
const CLINIC_SLIDES = [
  { src: "carousel-1.png", label: "The Reception",       caption: "Arrive to calm. Marble, orchids, and unhurried care." },
  { src: "carousel-2.png", label: "Your Furchild, Seen", caption: "Every patient treated with the attention they deserve." },
  { src: "carousel-3.png", label: "The Treatment Suite", caption: "State-of-the-art diagnostics in a space of serenity." },
  { src: "carousel-4.png", label: "Hands-On Devotion",   caption: "A gentle touch informed by genomic precision." },
];

const SLIDE_DURATION = 4800;

const slideVariants = {
  enter: (dir: number) => ({
    x: `${dir > 0 ? 6 : -6}%`,
    opacity: 0,
  }),
  center: {
    x: "0%",
    opacity: 1,
  },
  exit: (dir: number) => ({
    x: `${dir > 0 ? -3 : 3}%`,
    opacity: 0,
  }),
};

const slideTransition = {
  duration: 1.1,
  ease: [0.76, 0, 0.24, 1] as const,
};

function ClinicCarousel() {
  const [active, setActive]       = useState(0);
  const [direction, setDirection] = useState(1);
  const [hoverSide, setHoverSide] = useState<"left" | "right" | null>(null);
  const [progress, setProgress]   = useState(0);

  const editRef   = useRef<HTMLDivElement>(null);
  const animRef   = useRef<number>(0);
  const startRef  = useRef<number>(0);
  const pausedRef = useRef(false);

  const { scrollYProgress: editSP } = useScroll({ target: editRef, offset: ["start end", "end start"] });
  const editY = useTransform(editSP, [0, 1], [40, -40]);

  function advance(dir: 1 | -1) {
    cancelAnimationFrame(animRef.current);
    setDirection(dir);
    setActive(a => (a + dir + CLINIC_SLIDES.length) % CLINIC_SLIDES.length);
    setProgress(0);
    startRef.current = 0;
  }

  useEffect(() => {
    pausedRef.current = hoverSide !== null;
    if (hoverSide !== null) {
      cancelAnimationFrame(animRef.current);
      return;
    }
    startRef.current = 0;
    setProgress(0);

    const tick = (now: number) => {
      if (!startRef.current) startRef.current = now;
      const p = Math.min((now - startRef.current) / SLIDE_DURATION, 1);
      setProgress(p);
      if (p < 1) {
        animRef.current = requestAnimationFrame(tick);
      } else {
        setDirection(1);
        setActive(a => (a + 1) % CLINIC_SLIDES.length);
        startRef.current = 0;
      }
    };

    animRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animRef.current);
  }, [active, hoverSide]);

  return (
    <div
      className="aspect-[4/3] md:aspect-[3/4] w-full max-w-3xl md:ml-auto relative overflow-hidden bg-muted select-none cursor-none"
      ref={editRef}
      onMouseMove={e => {
        const r = e.currentTarget.getBoundingClientRect();
        setHoverSide(e.clientX - r.left < r.width / 2 ? "left" : "right");
      }}
      onMouseLeave={() => setHoverSide(null)}
      onClick={() => { if (hoverSide === "left") advance(-1); else advance(1); }}
    >

      {/* ── Per-slide progress segments ────────────────────────────── */}
      <div className="absolute top-0 left-0 right-0 z-20 flex gap-[2px] p-3 pointer-events-none">
        {CLINIC_SLIDES.map((_, i) => (
          <div key={i} className="flex-1 h-[1.5px] relative overflow-hidden rounded-full bg-background/20">
            {i < active && (
              <div className="absolute inset-0 bg-background/75" />
            )}
            {i === active && (
              <motion.div
                className="absolute inset-y-0 left-0 bg-background/75"
                style={{ width: `${progress * 100}%` }}
              />
            )}
          </div>
        ))}
      </div>

      {/* ── Slides ─────────────────────────────────────────────────── */}
      <AnimatePresence custom={direction} initial={false}>
        <motion.div
          key={active}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={slideTransition}
          className="absolute inset-0"
        >
          <div className="relative w-full h-full overflow-hidden">
            {/* Ken Burns: zoom out from 1.08 → 1.00 over 7 s per slide */}
            <motion.img
              src={`${import.meta.env.BASE_URL}${CLINIC_SLIDES[active].src}`}
              alt={CLINIC_SLIDES[active].label}
              className="object-cover w-full h-full origin-center"
              style={{ y: editY }}
              initial={{ scale: 1.08 }}
              animate={{ scale: 1.0 }}
              transition={{ duration: 7, ease: "easeOut" }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 via-foreground/10 to-transparent" />

            {/* ── Text block with staggered entrance ──────────────── */}
            <div className="absolute bottom-0 left-0 right-0 p-8 md:p-10">

              {/* Counter */}
              <motion.p
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25, duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-background/45 mb-3"
              >
                {String(active + 1).padStart(2, "0")} / {String(CLINIC_SLIDES.length).padStart(2, "0")}
              </motion.p>

              {/* Label — clip-path curtain lift */}
              <div className="overflow-hidden mb-2">
                <motion.h3
                  initial={{ y: "105%" }}
                  animate={{ y: "0%" }}
                  transition={{ delay: 0.42, duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
                  className="font-serif text-3xl md:text-4xl text-background leading-tight"
                >
                  {CLINIC_SLIDES[active].label}
                </motion.h3>
              </div>

              {/* Caption */}
              <motion.p
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.68, duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
                className="font-sans text-sm text-background/60 max-w-xs leading-relaxed"
              >
                {CLINIC_SLIDES[active].caption}
              </motion.p>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* ── Directional hover cue ──────────────────────────────────── */}
      <AnimatePresence>
        {hoverSide && (
          <motion.div
            key={hoverSide}
            initial={{ opacity: 0, x: hoverSide === "left" ? -8 : 8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: hoverSide === "left" ? -8 : 8 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className={`absolute top-1/2 -translate-y-1/2 z-10 pointer-events-none
              ${hoverSide === "left" ? "left-7" : "right-7"}`}
          >
            <span className="font-grotesk text-[9px] uppercase tracking-[0.3em] text-background/60">
              {hoverSide === "left" ? "←" : "→"}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}

/* ─── MARQUEE QUOTES ─────────────────────────────────────────────────────
   Reduced "FOR YOUR FURCHILD" repetition; two new brand slogans added.   */
const QUOTES = [
  { text: "Rescheduled my own dentist for Biscuit's wellness exam", author: "Shiadanni" },
  { text: "Oreo has better healthcare than most humans I know", author: "Marcus" },
  { text: "I budget Furbaby before rent. I'll do it again", author: "Priya" },
  { text: "Mochi asked for the Besotted Plan. She deserves it", author: "Jamie" },
  { text: "Furbaby knows my dog's name. My GP calls me 'the patient'", author: "Yan" },
  { text: "Bruno rejected generic kibble. Furbaby's genomics panel explained why", author: "Karan" },
  { text: "Told my boss it was a family emergency. Noodle's bloodwork panel doesn't lie", author: "Alex" },
  { text: "BECAUSE THEY CAN'T MAKE THE APPOINTMENT THEMSELVES.", author: "" },
  { text: "My cat has a better health plan than me and I'm completely at peace with that", author: "Preethi" },
  { text: "Furbaby remembered Kopi's birthday. My own mum forgot mine", author: "Wei" },
  { text: "Signed up for Besotted before I had renter's insurance. No regrets", author: "David" },
  { text: "Mango's genomics workup was more detailed than my last physical", author: "Sasha" },
  { text: "Pepper had a full genomics workup before I had a savings account", author: "Linh" },
  { text: "The waiting room smells better than most spas I've been to", author: "Clarisse" },
  { text: "I told my sister I couldn't afford her wedding gift. Tofu just needed his annual dental", author: "Ray" },
  { text: "Cookie has been through more Furbaby wellness exams than I've had GP visits combined", author: "Natasha" },
  { text: "My dog gets quarterly bloodwork panels at Furbaby. I get a yearly guilt about not flossing", author: "Jerome" },
  { text: "Furbaby sent a same-day wellness summary after Peanut's visit. My doctor never has", author: "Farah" },
  { text: "Latte is on a genomics-informed diet plan. I had instant noodles for dinner", author: "Hana" },
  { text: "I have no regrets. Waffles has a Furbaby health roadmap and that's final", author: "Cleo" },
  { text: "Dusty sneezed once and Furbaby had him seen within the hour. I once had a broken rib for a week", author: "Tom" },
  { text: "I cancelled a holiday to do Cheddar's longevity panel. Would do it again", author: "Isabelle" },
  { text: "Furbaby knows Dumpling's stress triggers from her health file. I didn't know I had stress triggers", author: "Min" },
  { text: "Remy has a DNA-based longevity supplement plan. I take whatever's on sale", author: "George" },
  { text: "The Furbaby portal knows more about my cat than I know about myself", author: "Sophie" },
  { text: "Nori gets a birthday message from Furbaby every year. My own inbox has promotions", author: "Kenji" },
  { text: "I explained the Besotted Plan to my accountant. She said nothing for a long time", author: "Diana" },
  { text: "Pudding is on a Furbaby longevity protocol. I just try to drink enough water", author: "Amara" },
  { text: "Furbaby remembered that Boba gets anxious near strangers. That's more than most people manage", author: "Tess" },
  { text: "Truffle had a wellness check on a public holiday. I couldn't get paracetamol", author: "Ben" },
  { text: "Furbaby's genomics report came with a 40-page PDF. I cried reading it. Proud parent moment", author: "Cici" },
  { text: "Sesame's Furbaby digital health passport travels with us internationally. Mine expired in 2019", author: "Rahul" },
  { text: "I told my parents I joined Furbaby membership for Miso. They've stopped asking questions", author: "Yuki" },
  { text: "Butterscotch has a dedicated Furbaby doctor who knows her by name. I'm just 'the owner'", author: "Lena" },
  { text: "Furbaby sent a same-day summary after every visit. My GP sends a letter three weeks later", author: "Olu" },
  { text: "Pretzel's annual dental was covered by the Devoted plan. Mine cost me three months of guilt", author: "Mei" },
  { text: "I joined Furbaby Besotted the week I got Nugget. Best financial decision I've made", author: "Sam" },
  { text: "Taro has a lower resting heart rate and better bloodwork than everyone in my household", author: "Pia" },
  { text: "My friends ask how I afford Furbaby. I ask how they afford not to", author: "Jordan" },
  { text: "Wonton is two years old and already has a full Furbaby genomic baseline. I'm proud and slightly jealous", author: "Adele" },
];

/* ─── MEMBERSHIP TIERS ───────────────────────────────────────────────────
   All pink shades (L=light → R=dark). Removed "dark" style.
   Renamed "The Nurturer" → "The Steadfast". Fixed features copy.        */
const TIERS = [
  {
    name: "Essential",
    whimsy: "The Steadfast",
    tagline: "For the conscientious furchild parent",
    price: "S$680",
    monthly: "S$57",
    tag: null,
    gradientFrom: "hsl(338,20%,98%)",
    gradientTo: "hsl(338,36%,95%)",
    titleColor: "hsl(338, 38%, 60%)",
    titleHover: "hsl(338, 42%, 48%)",
    features: [
      "Annual wellness examination",
      "Core vaccinations",
      "Genomics baseline screening",
      "Same-week appointment guarantee",
      "Personalised health record, accessible anytime",
    ],
    cta: "Explore Steadfast",
  },
  {
    name: "Core",
    whimsy: "The Devoted",
    tagline: "For those who leave nothing to chance",
    price: "S$1,180",
    monthly: "S$98",
    tag: "Most Popular",
    gradientFrom: "hsl(338,22%,97%)",
    gradientTo: "hsl(338,40%,93%)",
    titleColor: "hsl(338, 52%, 42%)",
    titleHover: "hsl(338, 55%, 32%)",
    features: [
      "Bi-annual examinations",
      "Comprehensive bloodwork panel",
      "Advanced Genomics Panel — included",
      "One urgent care consultation per year",
      "Dental health screening",
      "Priority 24hr booking",
    ],
    cta: "Explore Devoted",
  },
  {
    name: "Plus",
    whimsy: "The Besotted",
    tagline: "For parents who know no bounds",
    price: "S$2,980",
    monthly: "S$248",
    tag: "All-inclusive",
    gradientFrom: "hsl(338,24%,97%)",
    gradientTo: "hsl(338,44%,91%)",
    titleColor: "hsl(248, 40%, 22%)",
    titleHover: "hsl(248, 40%, 14%)",
    features: [
      "Weekly consultations",
      "Full diagnostic suite access",
      "Full Genomics Workup — included",
      "24/7 veterinary care line",
      "Annual dental procedure",
      "Daily wellness checks and specialist access",
    ],
    cta: "Explore Besotted",
  },
];

/* ─── FOOTER ─────────────────────────────────────────────────────────────── */
function Footer() {
  const footerRef = useRef<HTMLElement>(null);
  const isInView  = useInView(footerRef, { once: true, amount: 0 });

  return (
    <footer ref={footerRef} className="pt-40 pb-12 px-4 md:px-12 bg-primary flex flex-col items-center justify-center relative">
      <div
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{
          background: "radial-gradient(ellipse at 30% 50%, hsl(270 60% 85% / 1) 0%, transparent 60%), radial-gradient(ellipse at 70% 50%, hsl(338 70% 90% / 1) 0%, transparent 60%)"
        }}
      />

      <div className="flex justify-center z-10 mb-16 overflow-hidden">
        {"FURBABY".split("").map((letter, i) => (
          <motion.span
            key={i}
            className="font-serif text-[22vw] leading-[0.72] tracking-tighter uppercase text-foreground/60 inline-block"
            initial={{ y: "110%" }}
            animate={isInView ? { y: "0%" } : { y: "110%" }}
            transition={{ duration: 1.3, delay: i * 0.07, ease: [0.16, 1, 0.3, 1] }}
          >
            <motion.span
              className="inline-block"
              animate={isInView ? { y: [0, -6, 0] } : {}}
              transition={{ duration: 4, delay: 1.4 + i * 0.15, repeat: Infinity, ease: "easeInOut" }}
            >
              {letter}
            </motion.span>
          </motion.span>
        ))}
      </div>

      <motion.div
        className="flex flex-col md:flex-row justify-between w-full items-center gap-8 pt-12 border-t border-foreground/15 font-grotesk text-xs uppercase tracking-widest z-10"
        initial={{ opacity: 0, y: 16 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.9, delay: 0.3 }}
      >
        <span>© 2024 Furbaby Vet Care · Singapore</span>
        <motion.span
          className="font-serif italic normal-case tracking-normal text-foreground/35 text-xs"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 1.2, delay: 2 }}
        >
          For Heki, Wendigo, and Charlie
        </motion.span>
        <div className="flex gap-8 md:gap-12">
          <Link href="/furgenomics" className="hover:opacity-50 transition-opacity duration-300 cursor-none">FurGenomics</Link>
          <Link href="/journal" className="hover:opacity-50 transition-opacity duration-300 cursor-none">Journal</Link>
          <Link href="/privacy" className="hover:opacity-50 transition-opacity duration-300 cursor-none">Privacy</Link>
        </div>
      </motion.div>
    </footer>
  );
}

/* ─── HOME PAGE ─────────────────────────────────────────────────────────── */
export default function Home() {
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const heroBlockY = useTransform(scrollYProgress, [0, 0.35], [0, -120]);
  const pawRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress: pawSP } = useScroll({ target: pawRef, offset: ["start end", "end start"] });
  const pawY = useTransform(pawSP, [0, 1], [80, -80]);
  const servicesGridRef = useRef<HTMLDivElement>(null);
  const servicesInView = useInView(servicesGridRef, { once: true, amount: 0.05 });

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.02, smoothWheel: true });
    let rafId: number;
    function raf(time: number) { lenis.raf(time); rafId = requestAnimationFrame(raf); }
    rafId = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(rafId); lenis.destroy(); };
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden selection:bg-primary/40 selection:text-foreground cursor-none">
      <PageMeta
        title="Furbaby Vet Care — Vet Care Reimagined"
        description="Furbaby is a premium veterinary clinic opening in Holland Village, Singapore. Founding member waitlist now open — join 2,000 furchild parents redefining pet care."
        canonical="https://furbaby.vet/main/"
        ogTitle="Furbaby — Vet Care Reimagined"
        ogDescription="A new kind of veterinary clinic. Opening in Holland Village, Singapore. Founding member waitlist now open."
      />
      <Cursor />

      {/* Scroll progress */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-[1px] bg-primary/60 z-[100] origin-left"
        style={{ scaleX: scrollYProgress }}
      />

      <WaitlistModal open={waitlistOpen} onClose={() => setWaitlistOpen(false)} />

      {/* ─── NAV ──────────────────────────────────────────────── */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-6 py-6 flex justify-between items-center pointer-events-none">
        <motion.div
          className="font-serif text-2xl font-bold italic tracking-tighter pointer-events-auto cursor-none"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.6 }}
        >
          Furbaby
        </motion.div>
        <motion.div
          className="pointer-events-auto flex items-center gap-6"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.8 }}
        >
          <button
            onClick={() => setWaitlistOpen(true)}
            className="group flex items-center gap-2 font-grotesk text-xs uppercase tracking-widest hover:opacity-60 transition-opacity duration-500 cursor-none"
            data-testid="link-join-waitlist"
          >
            Join the Waitlist
            <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform duration-500" />
          </button>
        </motion.div>
      </nav>

      {/* ─── HERO ─────────────────────────────────────────────── */}

      {/* MOBILE hero — stacked: full-width image on top, large text below */}
      <section className="md:hidden w-full flex flex-col pt-16 overflow-hidden">
        {/* Full-width panning image — landscape crop keeps it compact */}
        <motion.div
          className="w-full overflow-hidden"
          initial={{ scaleY: 0, originY: 0 }}
          animate={{ scaleY: 1 }}
          transition={{ duration: 1.4, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
        >
          <HeroMobileImage />
        </motion.div>

        {/* Mobile text — one word per line, fills width */}
        <div className="flex flex-col px-4 pt-5 pb-10">
          {[
            { text: "VET",        size: "text-[21vw]" },
            { text: "CARE",       size: "text-[21vw]" },
            { text: "REIMAGINED", size: "text-[11.5vw]", period: true },
          ].map(({ text, size, period }, i) => (
            <div key={text} className="overflow-hidden">
              <motion.h1
                className={`font-serif ${size} leading-[0.88] tracking-tighter uppercase`}
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.3, delay: 0.5 + i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              >
                {text}{period && <span className="text-[0.48em]">.</span>}
              </motion.h1>
            </div>
          ))}
          <motion.p
            className="font-grotesk text-[10px] uppercase tracking-[0.3em] mt-6 opacity-40 leading-relaxed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.4 }}
            transition={{ duration: 1, delay: 1.4 }}
          >
            for your furchild
          </motion.p>
          <motion.button
            onClick={() => setWaitlistOpen(true)}
            className="group flex items-center gap-3 mt-8 font-grotesk text-xs uppercase tracking-widest border-b border-foreground/30 pb-2 hover:opacity-60 transition-opacity duration-500 cursor-none"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.6 }}
          >
            Join the founding member waitlist
            <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform duration-500" />
          </motion.button>
        </div>
      </section>

      {/* DESKTOP hero — image sets the height, text sits alongside */}
      <section className="hidden md:flex relative w-full overflow-hidden">
        {/* Left — text, vertically centred, padded for nav */}
        <div className="flex-1 flex flex-col justify-center px-12 pt-24 pb-16 z-10">
          <div className="flex flex-col">
            {[
              { text: "VET CARE",   indent: false },
              { text: "REIMAGINED", indent: true, period: true },
            ].map(({ text, indent, period }, i) => (
              <div key={text} className="overflow-hidden">
                <motion.h1
                  className={`font-serif text-[8.5vw] leading-[0.84] tracking-tighter uppercase${indent ? " ml-[5vw]" : ""}`}
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1.3, delay: 0.25 + i * 0.16, ease: [0.16, 1, 0.3, 1] }}
                >
                  {text}{period && <span className="text-[0.48em]">.</span>}
                </motion.h1>
              </div>
            ))}
          </div>
        </div>

        {/* Tagline + CTA — bottom-left of text area */}
        <motion.div
          className="absolute bottom-10 left-12 z-10 flex flex-col gap-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 2 }}
        >
          <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] opacity-45">
            for your furchild
          </p>
          <button
            onClick={() => setWaitlistOpen(true)}
            className="group flex items-center gap-3 font-grotesk text-xs uppercase tracking-widest border-b border-foreground/30 pb-2 w-fit hover:opacity-60 transition-opacity duration-500 cursor-none"
          >
            Join the founding member waitlist
            <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform duration-500" />
          </button>
        </motion.div>

        {/* Right — image panel, natural aspect ratio drives section height */}
        <motion.div
          className="w-[38%] flex-shrink-0 relative overflow-hidden"
          style={{ y: heroBlockY }}
          initial={{ scaleY: 0 }}
          animate={{ scaleY: 1 }}
          transition={{ duration: 1.5, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
        >
          {/* Aspect-ratio sizer — adjust to match wall-4 proportions */}
          <div className="w-full" style={{ paddingBottom: "130%" }} />
          <div className="absolute inset-0">
            <HeroImage />
            <div className="absolute inset-0 bg-primary/10" />
          </div>
        </motion.div>
      </section>

      {/* ─── MARQUEE ──────────────────────────────────────────── */}
      <div className="bg-primary py-5 overflow-hidden border-y border-primary/30 flex whitespace-nowrap group">
        {/* Duplicate once for seamless loop seam — 40 quotes is plenty */}
        <div className="animate-marquee group-hover:[animation-play-state:paused] inline-flex shrink-0 font-grotesk text-sm tracking-[0.15em] uppercase text-foreground">
          {[...QUOTES, ...QUOTES].map((q, i) => (
            <span key={i} className={`mx-8 shrink-0 ${q.author ? "opacity-70" : "font-semibold opacity-100"}`}>
              {q.text}{q.author ? <span className="text-foreground/40 text-xs ml-2">— {q.author}</span> : ""}&ensp;·
            </span>
          ))}
        </div>
      </div>

      {/* ─── BRAND STORY ──────────────────────────────────────── */}
      <section className="py-20 md:py-40 px-4 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-center bg-background">
        <div className="md:col-span-8 md:col-start-3 flex flex-col gap-10">
          {/* Line drawing — above the quote */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
            className="w-56 md:w-72 select-none pointer-events-none"
          >
            <img
              src={`${import.meta.env.BASE_URL}paw-line.jpg`}
              alt=""
              aria-hidden="true"
              className="w-full h-auto"
              style={{ mixBlendMode: "multiply", opacity: 0.45 }}
            />
          </motion.div>

          {/* Quote */}
          <div className="overflow-hidden">
            <motion.p
              initial={{ y: 70, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
              className="font-serif text-4xl md:text-6xl leading-[1.1] tracking-tight"
            >
              We believe that veterinary care should be as refined as the love
              you have for your furchild. No clinical coldness. Just{" "}
              <span className="italic text-primary">attentiveness, care, and precision.</span>
            </motion.p>
          </div>

          {/* Terrazzo paw — right-aligned within the column */}
          <div className="flex justify-end mt-20" ref={pawRef}>
            <motion.div
              initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
              whileInView={{ clipPath: "inset(0% 0% 0% 0%)" }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.3, ease: [0.16, 1, 0.3, 1] }}
              className="w-72 h-[450px] relative overflow-hidden bg-accent/30"
            >
              <motion.img
                src={`${import.meta.env.BASE_URL}paw-detail.png`}
                alt="Paw detail"
                className="object-cover w-full h-full mix-blend-multiply opacity-70"
                style={{ y: pawY, scale: 1.18 }}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── SERVICES ─────────────────────────────────────────── */}
      <section className="bg-accent text-accent-foreground py-20 md:py-40 px-4 md:px-12 relative overflow-hidden">
        <div className="hidden md:block absolute top-12 right-4 [writing-mode:vertical-rl]">
          <span className="font-grotesk text-[10px] uppercase tracking-[0.3em] opacity-50">Services</span>
        </div>

        {/* Wellness USP blurb — top of section, serif italic in primary */}
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="font-serif italic text-2xl md:text-3xl text-accent-foreground/80 max-w-2xl mb-16 leading-snug"
        >
          Every furchild is unique. Our care protocols are built around that individuality — not a breed average, not a textbook template. Using cutting-edge genomics, every treatment is tailored specifically to your furbaby.
        </motion.p>

        <div className="overflow-hidden mb-16">
          <motion.h2
            initial={{ y: "110%" }}
            whileInView={{ y: "0%" }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif text-[15vw] md:text-[120px] tracking-tighter uppercase leading-none"
          >
            Our <br /> Practices
          </motion.h2>
        </div>

        <div ref={servicesGridRef} className="grid grid-cols-1 md:grid-cols-2 gap-y-32 gap-x-20 relative z-10 max-w-6xl mx-auto">
          {[
            {
              num: "01", title: "Wellness",
              desc: "Preventative care built around your furchild's individual biology, lifestyle, and age — never a one-size-fits-all protocol."
            },
            {
              num: "02", title: "Diagnostics",
              desc: "State-of-the-art imaging and pathology for uncompromising precision."
            },
            {
              num: "03", title: "Genomics",
              desc: "Personalised care based on your furchild's DNA markers. We use genomics to map every biological marker unique to your pet, and ensure treatments are based on your furchild's own genome — not guesswork."
            },
            {
              num: "04", title: "Longevity",
              desc: "Natural supplements and advanced medications designed to extend your furchild's vitality — naturally and measurably."
            },
          ].map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, x: i % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 1, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="flex flex-col md:flex-row gap-8 items-start group"
            >
              <span className="font-grotesk text-2xl font-light text-foreground/30 mt-1 group-hover:text-foreground/60 transition-colors duration-500">
                {s.num}
              </span>
              <div>
                <motion.h3
                  initial={{ opacity: 0, y: 20 }}
                  animate={servicesInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ duration: 0.9, delay: 0.3 + i * 0.15, ease: [0.16, 1, 0.3, 1] }}
                  className="font-serif text-5xl group-hover:italic transition-all duration-500 text-background/85 mb-5"
                >
                  {s.title}
                </motion.h3>
                <p className="font-sans text-xl opacity-75 max-w-md leading-relaxed">{s.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ─── CLINIC SPACE ─────────────────────────────────────── */}
      <section className="py-20 md:py-40 px-4 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 relative bg-background">
        <div className="md:col-span-5 relative z-10 pt-20">
          <div className="overflow-hidden mb-12">
            <motion.h2
              initial={{ y: "110%" }}
              whileInView={{ y: "0%" }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              className="font-serif text-6xl md:text-[100px] leading-[0.9] tracking-tighter uppercase"
            >
              The <br /> Clinic <br /> <span className="italic text-primary">Space.</span>
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 0.70, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="font-sans text-xl max-w-md mb-16 leading-relaxed"
          >
            Designed to soothe rather than stress. Terrazzo floors, ambient
            lighting, and acoustic dampening create an environment where anxiety
            dissipates.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <Link
              href="/book"
              className="group inline-flex items-center gap-4 font-grotesk text-sm uppercase tracking-widest hover:opacity-60 transition-opacity duration-500 border-b border-foreground pb-2 cursor-none"
            >
              Book a Visit
              <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform duration-500" />
            </Link>
          </motion.div>
        </div>

        <motion.div
          className="md:col-span-7 relative"
          initial={{ clipPath: "inset(0% 0% 100% 0%)" }}
          whileInView={{ clipPath: "inset(0% 0% 0% 0%)" }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
        >
          <ClinicCarousel />
        </motion.div>
      </section>

      {/* ─── MEMBERSHIP ───────────────────────────────────────── */}
      <section className="bg-background py-10 md:py-40 px-4 md:px-12 relative overflow-hidden border-t border-border/50">
        <div className="hidden md:block absolute top-12 left-4 [writing-mode:vertical-rl] rotate-180">
          <span className="font-grotesk text-[15px] uppercase tracking-[0.3em] opacity-20">Exclusive</span>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-end mb-8 pb-8 md:mb-24 md:pb-16 border-b border-border/40">
          <div className="overflow-hidden">
            <motion.h2
              initial={{ y: "110%" }}
              whileInView={{ y: "0%" }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              className="font-serif text-5xl md:text-[140px] tracking-tighter uppercase leading-[0.8]"
            >
              Membership
            </motion.h2>
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.5 }}
            className="flex flex-col items-end gap-2 mt-6 md:mt-0"
          >
            <div className="rounded-xl px-4 py-3 md:px-8 md:py-6 flex flex-col gap-2 w-full md:w-96">
              <span className="font-grotesk text-sm uppercase tracking-wider opacity-50 leading-snug">
                Elevated care plans — genomics screening included in every tier
              </span>
              <span className="font-grotesk text-[10px] uppercase tracking-widest text-primary opacity-80">
                Billed annually
              </span>
            </div>
          </motion.div>
        </div>

        {/* Tier cards — all same style, pink shades only differentiate */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
          {TIERS.map((tier, i) => (
            <motion.div
              key={tier.name}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 1, delay: i * 0.14, ease: [0.16, 1, 0.3, 1] }}
              className="group relative flex flex-col p-6 md:p-10 rounded-[2rem] md:rounded-[3rem] overflow-hidden transition-all duration-700 border border-primary/20 hover:border-primary/40"
              style={{ background: `linear-gradient(135deg, ${tier.gradientFrom} 0%, ${tier.gradientTo} 100%)` }}
            >
              {/* Hover glow */}
              <div className="absolute inset-0 rounded-[3rem] opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-white/15 pointer-events-none" />

              {/* Fixed-height tag slot — keeps all cards aligned */}
              <div className="relative z-10 h-7 mb-4">
                {tier.tag && (
                  <span className="font-grotesk text-[9px] uppercase tracking-widest px-3 py-1.5 rounded-full bg-white/30 text-foreground/70">
                    {tier.tag}
                  </span>
                )}
              </div>

              <div className="relative z-10 mb-1">
                <p className="font-grotesk text-[9px] uppercase tracking-widest mb-1 text-foreground/40">
                  {tier.name}
                </p>
                <h3
                  className="font-serif text-4xl transition-all duration-500"
                  style={{ color: tier.titleColor }}
                >
                  {tier.whimsy}
                </h3>
                <p className="font-sans text-xs mt-1 opacity-40">{tier.tagline}</p>
              </div>

              <div className="relative z-10 mt-5 mb-2">
                <div className="flex items-baseline gap-2">
                  <span className="font-serif text-4xl" style={{ color: tier.titleColor }}>
                    {tier.monthly}
                  </span>
                  <span className="font-grotesk text-sm uppercase tracking-widest opacity-75 font-medium">/ month</span>
                </div>
                <p className="relative z-10 font-grotesk text-[10px] uppercase tracking-widest mt-2 mb-10 opacity-55">
                  {tier.price} · billed as one annual payment
                </p>
              </div>

              <ul className="relative z-10 flex flex-col gap-5 mb-10 flex-grow">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 font-sans text-sm leading-snug">
                    <span
                      className="block w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 transition-colors duration-500"
                      style={{ backgroundColor: tier.titleColor }}
                    />
                    <span className="opacity-65 group-hover:opacity-90 transition-colors duration-500">{f}</span>
                  </li>
                ))}
              </ul>

              <div className="relative z-10 mt-auto">
                <Link
                  href="/membership"
                  className="group/btn inline-flex items-center gap-3 font-grotesk text-sm uppercase tracking-widest hover:opacity-60 transition-opacity duration-500 border-b border-foreground/20 pb-2 w-fit cursor-none"
                  data-testid={`link-join-${tier.name.toLowerCase()}`}
                >
                  {tier.cta}
                  <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-2 transition-transform duration-500" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ─── FOOTER ───────────────────────────────────────────── */}
      <Footer />
    </div>
  );
}
